<script>
  import { createEventDispatcher } from 'svelte';
  import { X, Tag } from 'lucide-svelte';
  import { productsApi, variantConditionPricesApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import RichTextEditor from './RichTextEditor.svelte';

  export let show = false;
  export let variant = null;
  export let productId = null;

  let loading = false;
  let error = '';

  // Form fields
  let formData = {
    code: '',
    name: '',
    description: '',
    price: '',
    unit: '',
    billingCycle: 'one_time',
    category: 'service',
    isActive: true,
    isPublic: false
  };

  // Activity types linked to this variant
  let selectedActivityTypes = [];

  const CONDITIONS = ['weekend', 'holiday', 'urgent', 'night'];
  const HOURLY_UNITS = ['h', 'ora', 'ore', 'hour', 'hours'];
  // Exact text the backend returns for a duplicate (variant, condition) row (409).
  const CONDITION_PRICE_CONFLICT_MESSAGE = 'This variant already has a price for this condition';

  /** @type {Record<string, number|string>} */
  let conditionPrices = {};
  /** @type {Record<string, number>} */
  let conditionPriceIds = {};
  /** Last value persisted per condition, or '' when not offered. Lets blur skip a no-op request. @type {Record<string, number|string>} */
  let conditionLastSaved = {};
  /** Per-condition promise chain so a rapid edit-blur-edit-blur can't fire two concurrent creates. @type {Record<string, Promise<void>>} */
  let conditionSaveQueue = {};

  /**
   * @param {string|null} unit
   * @returns {boolean}
   */
  function isHourly(unit) {
    return !!unit && HOURLY_UNITS.includes(String(unit).trim().toLowerCase());
  }

  const CATEGORIES = [
    { value: 'service', label: 'Service' },
    { value: 'subscription', label: 'Subscription' },
    { value: 'hosting', label: 'Hosting' }
  ];

  const BILLING_CYCLES = [
    { value: 'one_time', label: 'One-time' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'bimonthly', label: 'Bimonthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const ACTIVITY_TYPES = [
    { value: 'development', label: 'Development' },
    { value: 'planning', label: 'Planning' },
    { value: 'admin', label: 'Admin' },
    { value: 'sales', label: 'Sales' },
    { value: 'communication', label: 'Communication' },
    { value: 'consulting', label: 'IT Consultancy' }
  ];

  const dispatch = createEventDispatcher();

  // Reset form when modal opens
  $: if (show) {
    if (variant) {
      // Edit mode
      formData = {
        code: variant.code || '',
        name: variant.name || '',
        description: variant.description || '',
        price: variant.price ? String(variant.price) : '',
        unit: variant.unit || '',
        billingCycle: variant.billingCycle || 'one_time',
        category: variant.category || 'service',
        isActive: variant.isActive !== false,
        isPublic: variant.isPublic === true
      };
      // Load activity types for this variant
      loadActivityTypes();
      // Load hourly surcharges for this variant
      loadConditionPrices();
    } else {
      // Create mode
      resetForm();
    }
    error = '';
  }

  async function loadActivityTypes() {
    if (!variant || !productId) {
      selectedActivityTypes = [];
      return;
    }
    try {
      const types = await productsApi.getVariantActivityTypes(productId, variant.id);
      selectedActivityTypes = types || [];
      console.log('📋 Loaded activity types:', selectedActivityTypes);
    } catch (err) {
      console.error('❌ Failed to load activity types:', err);
      selectedActivityTypes = [];
    }
  }

  async function loadConditionPrices() {
    if (!variant?.id) return;
    try {
      const rows = await variantConditionPricesApi.getByVariant(variant.id);
      conditionPrices = {};
      conditionPriceIds = {};
      conditionLastSaved = {};
      conditionSaveQueue = {};
      for (const row of rows) {
        conditionPrices[row.conditionCode] = row.price;
        conditionPriceIds[row.conditionCode] = row.id;
        conditionLastSaved[row.conditionCode] = row.price;
      }
    } catch (err) {
      console.error('[ProductVariantModal] ❌ Failed to load condition prices:', err);
      conditionPrices = {};
      conditionPriceIds = {};
      conditionLastSaved = {};
      conditionSaveQueue = {};
      error = $_('products.variants.conditionPrices.loadError');
    }
  }

  /**
   * Saves, updates or clears one surcharge. Serialized per condition so a
   * rapid edit-blur-edit-blur on a not-yet-persisted row cannot fire two
   * concurrent creates: the second call always waits for the first to
   * settle and then reads the live bound value, not one captured at blur
   * time.
   * @param {string} condition
   * @returns {Promise<void>}
   */
  function saveConditionPrice(condition) {
    const next = (conditionSaveQueue[condition] || Promise.resolve())
      .catch(() => {})
      .then(() => persistConditionPrice(condition));
    conditionSaveQueue[condition] = next;
    return next;
  }

  /**
   * Actually persists one surcharge. An empty field means the condition is
   * not offered, which is a delete, not a zero. A no-op is skipped when the
   * value has not changed since the last save.
   * @param {string} condition
   */
  async function persistConditionPrice(condition) {
    const raw = conditionPrices[condition];
    const isEmpty = raw === '' || raw === null || raw === undefined;
    const lastSaved = conditionLastSaved[condition];
    const lastSavedIsEmpty = lastSaved === undefined || lastSaved === '';
    const existingId = conditionPriceIds[condition];

    if (isEmpty) {
      if (lastSavedIsEmpty) return; // nothing to do, field was already empty
      if (!existingId) {
        conditionLastSaved[condition] = '';
        return;
      }
      try {
        await variantConditionPricesApi.delete(existingId);
        delete conditionPriceIds[condition];
        conditionLastSaved[condition] = '';
      } catch (err) {
        console.error('[ProductVariantModal] ❌ Condition price delete failed', err);
        error = err.message || $_('products.variants.conditionPrices.deleteError');
      }
      return;
    }

    const price = parseFloat(String(raw).replace(',', '.'));
    if (!Number.isFinite(price) || price < 0) {
      // Invalid input: reject visibly instead of silently no-op'ing while
      // the field still shows the rejected value.
      error = $_('products.variants.conditionPrices.saveError');
      return;
    }

    if (!lastSavedIsEmpty && lastSaved === price) return; // unchanged, skip the request

    try {
      if (existingId) {
        await variantConditionPricesApi.update(existingId, { price });
      } else {
        const created = await variantConditionPricesApi.create({
          variantId: variant.id,
          conditionCode: condition,
          price
        });
        conditionPriceIds[condition] = created.id;
      }
      conditionLastSaved[condition] = price;
    } catch (err) {
      if (!existingId && err.message === CONDITION_PRICE_CONFLICT_MESSAGE) {
        // Another create for this (variant, condition) won the race (e.g. a
        // second tab). Adopt the real row and apply the pending value as an
        // update instead of leaving the user's edit unsaved.
        try {
          const rows = await variantConditionPricesApi.getByVariant(variant.id);
          const match = rows.find(row => row.conditionCode === condition);
          if (match) {
            conditionPriceIds[condition] = match.id;
            await variantConditionPricesApi.update(match.id, { price });
            conditionLastSaved[condition] = price;
            return;
          }
        } catch (retryErr) {
          console.error('[ProductVariantModal] ❌ Condition price conflict recovery failed', retryErr);
        }
      }
      console.error('[ProductVariantModal] ❌ Condition price save failed', err);
      error = err.message || $_('products.variants.conditionPrices.saveError');
    }
  }

  function toggleActivityType(type) {
    if (selectedActivityTypes.includes(type)) {
      selectedActivityTypes = selectedActivityTypes.filter(t => t !== type);
    } else {
      selectedActivityTypes = [...selectedActivityTypes, type];
    }
  }

  function resetForm() {
    formData = {
      code: '',
      name: '',
      description: '',
      price: '',
      unit: '',
      billingCycle: 'one_time',
      category: 'service',
      isActive: true,
      isPublic: false
    };
    selectedActivityTypes = [];
    conditionPrices = {};
    conditionPriceIds = {};
    conditionLastSaved = {};
    conditionSaveQueue = {};
  }

  function handleClose() {
    if (loading) return;
    show = false;
    resetForm();
    dispatch('close');
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && !loading) {
      handleClose();
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!productId) {
      error = 'Product ID is missing';
      return;
    }

    try {
      loading = true;
      error = '';

      // Validate required fields
      if (!formData.code.trim()) {
        error = 'Code is required';
        return;
      }

      if (!formData.name.trim()) {
        error = 'Name is required';
        return;
      }

      // Prepare data
      const data = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        price: formData.price.trim() ? parseFloat(formData.price.replace(',', '.')) : null,
        unit: formData.unit.trim() || null,
        billingCycle: formData.billingCycle,
        category: formData.category,
        isActive: formData.isActive,
        isPublic: formData.isPublic
      };

      // Validate numeric fields
      if (data.price !== null && isNaN(data.price)) {
        error = 'Price must be a valid number';
        return;
      }

      console.log('💾 Saving variant:', data);

      let result;
      if (variant) {
        // Update existing variant
        result = await productsApi.updateVariant(productId, variant.id, data);
        console.log('✅ Variant updated:', result);
      } else {
        // Create new variant
        result = await productsApi.createVariant(productId, data);
        console.log('✅ Variant created:', result);
      }

      // Save activity types
      const variantId = result.id;
      await productsApi.setVariantActivityTypes(productId, variantId, selectedActivityTypes);
      console.log('✅ Activity types saved:', selectedActivityTypes);

      // Success
      dispatch('success', result);
      handleClose();

    } catch (err) {
      error = err.message;
      console.error('❌ Save variant error:', err);
    } finally {
      loading = false;
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if show}
  <!-- Modal backdrop -->
  <div class="modal-backdrop fade-in">
    <!-- Modal -->
    <div class="modal-content max-w-2xl w-full scale-in">
      <!-- Header -->
      <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700 flex items-center justify-between bg-linear-to-r from-primary-50/50 dark:from-primary-900/30 to-transparent">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
            <Tag class="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 class="text-lg font-bold text-gray-900 dark:text-white">
              {variant ? 'Edit Variant' : 'New Variant'}
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {variant ? 'Update variant details' : 'Add a new variant to the product'}
            </p>
          </div>
        </div>
        {#if !loading}
          <button
            onclick={handleClose}
            class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
          >
            <X size={20} />
          </button>
        {/if}
      </div>

      <!-- Content -->
      <form onsubmit={handleSubmit}>
        <div class="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {#if error}
            <div class="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-3">
              <div class="text-sm text-red-800 dark:text-red-200">{error}</div>
            </div>
          {/if}

          <div class="space-y-4">
            <!-- Code and Name -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label for="code" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Code *
                </label>
                <input
                  id="code"
                  type="text"
                  bind:value={formData.code}
                  placeholder="e.g. MAINT-1M"
                  class="input w-full"
                  disabled={loading || (variant !== null)}
                  required
                />
                {#if variant}
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Code cannot be modified</p>
                {/if}
              </div>

              <div>
                <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  id="name"
                  type="text"
                  bind:value={formData.name}
                  placeholder="e.g. Monthly"
                  class="input w-full"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <!-- Description -->
            <div>
              <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <RichTextEditor
                value={formData.description}
                placeholder="Detailed variant description"
                disabled={loading}
                on:change={(e) => formData.description = e.detail}
              />
            </div>

            <!-- Price and Unit -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label for="price" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Price
                </label>
                <div class="relative">
                  <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">€</span>
                  <input
                    id="price"
                    type="text"
                    bind:value={formData.price}
                    placeholder="0.00"
                    class="input w-full pl-8"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label for="unit" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Unit
                </label>
                <input
                  id="unit"
                  type="text"
                  bind:value={formData.unit}
                  placeholder="e.g. hour, year, month"
                  class="input w-full"
                  disabled={loading}
                />
              </div>
            </div>

            <!-- Hourly Surcharges -->
            {#if variant?.id && isHourly(formData.unit)}
              <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{$_('products.variants.conditionPrices.title')}</h4>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">{$_('products.variants.conditionPrices.hint')}</p>
                <div class="grid grid-cols-2 gap-3">
                  {#each CONDITIONS as condition}
                    <label class="block" for={`condition-${condition}`}>
                      <span class="text-xs text-gray-600 dark:text-gray-400">{$_(`products.variants.conditionPrices.${condition}`)}</span>
                      <input
                        id={`condition-${condition}`}
                        type="number"
                        step="0.01"
                        min="0"
                        bind:value={conditionPrices[condition]}
                        onblur={() => saveConditionPrice(condition)}
                        placeholder={formData.price || ''}
                        class="input w-full"
                        disabled={loading}
                      />
                    </label>
                  {/each}
                </div>
              </div>
            {/if}

            <!-- Billing Cycle and Category -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label for="billingCycle" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Billing Cycle
                </label>
                <select
                  id="billingCycle"
                  bind:value={formData.billingCycle}
                  class="input w-full"
                  disabled={loading}
                >
                  {#each BILLING_CYCLES as cycle}
                    <option value={cycle.value}>{cycle.label}</option>
                  {/each}
                </select>
              </div>

              <div>
                <label for="category" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  id="category"
                  bind:value={formData.category}
                  class="input w-full"
                  disabled={loading}
                >
                  {#each CATEGORIES as cat}
                    <option value={cat.value}>{cat.label}</option>
                  {/each}
                </select>
              </div>
            </div>

            <!-- Activity Types (for timesheet rate lookup) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Activity Types
              </label>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Link this variant to activity types for automatic rate lookup in timesheets
              </p>
              <div class="flex flex-wrap gap-2">
                {#each ACTIVITY_TYPES as at}
                  <button
                    type="button"
                    onclick={() => toggleActivityType(at.value)}
                    class="px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                      {selectedActivityTypes.includes(at.value)
                        ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}"
                    disabled={loading}
                  >
                    {at.label}
                  </button>
                {/each}
              </div>
            </div>

            <!-- Active Status -->
            <div class="flex items-center space-x-3">
              <input
                id="isActive"
                type="checkbox"
                bind:checked={formData.isActive}
                class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                disabled={loading}
              />
              <label for="isActive" class="text-sm font-medium text-gray-700 dark:text-gray-300">
                Active variant
              </label>
            </div>

            <!-- Public Visibility -->
            <div>
              <div class="flex items-center space-x-3">
                <input
                  id="isPublic"
                  type="checkbox"
                  bind:checked={formData.isPublic}
                  class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                  disabled={loading}
                />
                <label for="isPublic" class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {$_('products.variants.isPublic')}
                </label>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{$_('products.variants.isPublicHint')}</p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            type="button"
            onclick={handleClose}
            class="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={loading || !formData.code.trim() || !formData.name.trim()}
          >
            {#if loading}
              <svg class="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            {/if}
            {variant ? 'Update' : 'Create Variant'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}
